import ArtistProfileHero from "@/Components/User/ArtistProfile/ArtistProfileHero";
import ArtistProfileInfo from "@/Components/User/ArtistProfile/ArtistProfileInfo";
import ArtistProfileTabView from "@/Components/User/ArtistProfile/ArtistProfileTabView";
import Container from "@/Components/User/Common/Container";
import User from "@/Layouts/User";
import { useRouter } from "next/router";
import { useRef, useState } from "react";
import MetaTags from "@/Components/SEO/MetaTags";
import { Schema } from "@/Components/SEO/Schema";
import withBaseUrl from "@/Utils/const";
import { motion } from "framer-motion";
import { useLoading } from "@/context/loading";

export default function index({ artistData, tracks }) {
  const router = useRouter();
  const scrollRef = useRef(null);
  const [selection, setSelection] = useState(null);
  const { isLoading } = useLoading();

  const getBreadcrumbs = (artistData) => {
    if (!artistData?.data) return null;

    return [
      {
        name: "Home",
        url: withBaseUrl("/"),
      },
      {
        name: "Artists",
        url: withBaseUrl("/artists"),
      },
      {
        name: artistData.data.name,
        url: withBaseUrl(
          `/artist/${artistData.data.name?.toLowerCase().replace(/\s+/g, "-")}`
        ),
      },
    ];
  };

  return (
    <User ref={selection ? scrollRef : null}>
      <MetaTags
        title={`${artistData?.data?.name || "Artist"} - Qoncert`}
        description={`Discover ${
          artistData?.data?.name
        }'s upcoming shows, tour dates, and concert tickets on Qoncert. ${
          artistData?.data?.bio || ""
        }`}
        keywords={`${
          artistData?.data?.name
        }, concert tickets, live music, tour dates, ${
          artistData?.data?.name
        } tour, music events, ${artistData?.data?.genre || ""}`}
        ogImage={artistData?.data?.profile}
        ogType="profile"
        canonicalUrl={`/artist/${artistData?.data?.name
          ?.toLowerCase()
          .replace(/\s+/g, "-")}`}
      />
      <Schema breadcrumbs={getBreadcrumbs(artistData)} />
      <motion.div
        className="relative"
        initial={{ opacity: 0 }}
        animate={!isLoading ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          style={{
            backgroundImage: `url(${artistData?.data?.profile})`,
            filter: "blur(30px)",
            opacity: "0.75px",
            maxHeight: "510px",
            backgroundAttachment: "unset",
            marginTop: "-134px",
          }}
          className="layout_bg_image hidden lg:block"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={
            !isLoading
              ? { opacity: 0.75, scale: 1 }
              : { opacity: 0, scale: 1.1 }
          }
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span
            className="bg_inner_shadow"
            style={{ backgroundColor: "#00000080" }}
          ></span>
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={!isLoading ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <ArtistProfileHero info={artistData} />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={!isLoading ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <ArtistProfileInfo info={artistData} />
      </motion.div>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={!isLoading ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <ArtistProfileTabView
            setSelection={setSelection}
            info={artistData}
            artist={router?.query?.artist}
            ref={scrollRef}
            tracks={tracks}
          />
        </motion.div>
      </Container>
    </User>
  );
}

export async function getServerSideProps(context) {
  const { artist, slug } = context.params;

  let user_id = slug;

  try {
    const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
    const rawCookies = context.req.headers.cookie || "";
    let headers = {};

    const parsedCookies = Object.fromEntries(
      rawCookies.split("; ").map((cookie) => {
        const [key, ...rest] = cookie.split("=");
        return [key, rest.join("=")];
      })
    );

    let userData = {};
    if (parsedCookies.user_data) {
      try {
        userData = JSON.parse(decodeURIComponent(parsedCookies.user_data));
        headers = { Authorization: `Bearer ${userData?.web_token}` };
      } catch (error) {
        console.error("Error parsing user_data:", error);
      }
    }

    const formArtistData = new FormData();
    formArtistData.append("action", "getArtistData");
    formArtistData.append("username", user_id);
    const [data] = await Promise.all([
      fetch(`${BACKEND_URL}/webapi/manageUser`, {
        method: "POST",
        body: formArtistData,
        headers,
      }).then((res) => res.json()),
    ]);

    let tracks = [];

    if (data?.data?.id) {
      const tracksFormData = new FormData();
      tracksFormData.append("action", "getMusicListByArtist");
      tracksFormData.append("user_id", data?.data?.id);

      tracks = await fetch(`${BACKEND_URL}/webapi/manageMusic`, {
        method: "POST",
        body: tracksFormData,
        headers,
      }).then((res) => res.json());
    }

    if (!data.status) {
      return { notFound: true };
    }

    return {
      props: {
        artistData: data,
        tracks: tracks
      },
    };
  } catch (error) {
    console.error("Server error in getServerSideProps:", error);
    return {
      props: {
        artistData: null,
        error: "Something went wrong while fetching artist data.",
      },
    };
  }
}
